import {
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules';
import {
  GP_MODAL_VARIANT_ACKNOWLEDGED,
  type GatorProtectAcknowledgedModalFields,
} from './GatorProtectAcknowledgedModal.types';
import type { RootState } from '@/pages/Main/Main.types';
import useLocale from '@/hooks/useLocale';
import { useHistory } from 'react-router';
import type { GPModalsVariants } from '../gatorProtectModalsVisibilityReducer';
import type { HideGatorProtectModalSlot } from '../useGatorProtectModalsVisibility';
import type {
  GatorProtectGlobalsDependencies,
  LocaleWithRoutes,
} from '../GatorProtectGlobals.types';
import type { GatorProtectModalHookReturn } from '../gatorProtectModalHooks.types';
import { MONETIZACAO_GATOR_PROTECT_PRO_ACK_MODAL } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

export type UseGatorProtectAcknowledgedModalParams = {
  summary: GatorProtectGlobalsDependencies['summary'];
  isHome: boolean;
  hideModalSlot: HideGatorProtectModalSlot;
};

export type UseGatorProtectAcknowledgedModalData = {
  selectedAckedPlan: GatorProtectAcknowledgedModalFields['selectedPlan'];
};

export type UseGatorProtectAcknowledgedModalUtils = {
  hideAcknowledgedModal: (_topStackModalVariant: GPModalsVariants | null) => void;
  redirectAcknowledgedModal: () => void;
};

export type UseGatorProtectAcknowledgedModalReturn = GatorProtectModalHookReturn<
  UseGatorProtectAcknowledgedModalData,
  UseGatorProtectAcknowledgedModalUtils
>;

/**
 * Acknowledgment modal: first Redux `gatorProtect.plans` entry with `!is_acknowledged && !is_trial`,
 * plus `gatorProtectActions.acknowledgement.request` on dismiss / redirect.
 * Plans list fetch lives in `useGatorProtectGlobal`.
 * Dismiss-slot visibility is composed in `GatorProtectGlobals` (`visibilityRule && availableModals[variant]`).
 */
export function useGatorProtectAcknowledgedModal({
  summary,
  isHome,
  hideModalSlot,
}: UseGatorProtectAcknowledgedModalParams): UseGatorProtectAcknowledgedModalReturn {
  const dispatch = useDispatch();
  const history = useHistory();
  const { routes: routesLocale } = useLocale() as LocaleWithRoutes;
  const hasAcknowledgeModal = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_PRO_ACK_MODAL);

  const gatorProtectEnabled = summary.features?.gator_protect ?? false;

  const { plans: gatorProtectPlans } = useSelector((state: RootState) => state.gatorProtect);

  const selectedAcknowledgmentPlan = useMemo(
    () => gatorProtectPlans.find(p => !p.is_acknowledged && !p.is_free) ?? null,
    [gatorProtectPlans],
  );

  const selectedAckedPlan : GatorProtectAcknowledgedModalFields['selectedPlan'] | null = useMemo(() => {
    const p = selectedAcknowledgmentPlan;
    if (p == null) {
      return null;
    }
    return {
      expire_date: p.expire_date ?? '',
      expire_days: p.expire_days ?? null,
      is_trial: p.is_trial,
      shared_hosting_plan: p.shared_hosting_plan,
    };
  }, [selectedAcknowledgmentPlan]);

  const meetsAcknowledgmentRules = Boolean(isHome
    && gatorProtectEnabled
    && selectedAcknowledgmentPlan !== null
    && !selectedAcknowledgmentPlan?.is_free
    && hasAcknowledgeModal);

  const dispatchAcknowledgementIfNeeded = useCallback(() => {
    const hostingId = selectedAcknowledgmentPlan?.shared_hosting_id;
    if (hostingId != null) {
      dispatch(gatorProtectActions.acknowledgement.request({ hostingId }));
    }
  }, [dispatch, selectedAcknowledgmentPlan]);

  const hideAcknowledgedModal = useCallback((topStackModalVariant: GPModalsVariants | null) => {
    if (topStackModalVariant !== GP_MODAL_VARIANT_ACKNOWLEDGED) {
      return;
    }
    dispatchAcknowledgementIfNeeded();
    hideModalSlot(GP_MODAL_VARIANT_ACKNOWLEDGED);
  }, [dispatchAcknowledgementIfNeeded, hideModalSlot]);

  const redirectAcknowledgedModal = useCallback(() => {
    dispatchAcknowledgementIfNeeded();
    hideModalSlot(GP_MODAL_VARIANT_ACKNOWLEDGED);
    const base = routesLocale?.gatorProtect ?? '';
    const suffix = selectedAcknowledgmentPlan?.shared_hosting_id != null
      ? `/${selectedAcknowledgmentPlan.shared_hosting_id}`
      : '';
    history.push(`${base}${suffix}`);
  }, [
    dispatchAcknowledgementIfNeeded,
    hideModalSlot,
    history,
    routesLocale?.gatorProtect,
    selectedAcknowledgmentPlan,
  ]);

  return {
    data: { selectedAckedPlan },
    utils: {
      hideAcknowledgedModal,
      redirectAcknowledgedModal,
    },
    visibilityRule: meetsAcknowledgmentRules,
  };
}
